import React from "react";
import { Space, FileEntity } from "@highnote/server/src/core/entities";
import { RenderableTrack } from "App/store/tracks/buildSpaceTrackTile";
import { EntityRowConfig, ENTITY_TYPE, Version } from "./config";
import { ReactComponent as ChatSvg } from "App/common/icons-v2/chat.svg";
import { ReactComponent as ArrowUpRightSvg } from "App/common/icons-v2/external-link.svg";
import { ReactComponent as CaretRightSvg } from "App/common/icons/caret-right.svg";
import { useHistory } from "react-router";
import { Button, BUTTON_THEME } from "App/core/Button";
import {
  UserAvatarGroup,
  AvatarGroupUser,
} from "App/common/UserAvatar/UserAvatarGroup";
import { USER_AVATAR_SIZE } from "App/common/UserAvatar/UserAvatar";
import { useViewport } from "App/common/useViewport";
import { CellPlaceholder } from "./CellPlaceholder";
import "./EntityCTA.scss";

// Stops a CTA click from also triggering the row's onClick.
const stopRowClick = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

// Track: a facepile of recent commenters when the track has any, otherwise a
// comment button that invites starting a conversation. Both navigate to the
// track's comments. Always visible (not hover-gated).
const TrackCTA = ({
  entity,
  href,
}: {
  entity: RenderableTrack | Version;
  // Where the CTA navigates, already resolved against the VIEWING space by
  // `EntityCTA` below. `null` when no space could be resolved at all, which
  // is the only case that navigates nowhere — reading the origin off the
  // entity here is what produced `/space/undefined/...`, and what threw on a
  // Track with no `spaceId` (there is no `.track` to fall through to).
  href: string | null;
}) => {
  const history = useHistory();

  const navigate = (e: React.MouseEvent) => {
    stopRowClick(e);
    if (href) {
      history.push(href);
    }
  };

  // Facepile is driven by the denormalized recent-commenter fields: distinct
  // non-reaction comment authors (reactions excluded); uniqueCommenterCount
  // drives the "+N" overflow.
  //
  // A ROW-BACKED tile reads the `space-tracks-v1` row's own per-space summary
  // and nothing else. Two reasons, and either alone is enough:
  //  - Comments are per `(spaceId, trackId)`, so a borrowed tile must show
  //    the borrowing space's commenters, not the origin's.
  //  - `SHARE_TRACK` nulls the canonical doc's three legacy summary fields in
  //    the same write that sets `sharedOut` (`shareTrack.ts`), because that
  //    document becomes readable far more widely once a share exists. The row
  //    summary is NOT nulled, so this is what keeps the ORIGIN space's own
  //    facepile alive while its track is shared out.
  // A plain `Track` (Search, Library, the legacy space query) has no row, so
  // it keeps reading its own fields, exactly as before.
  const tile = entity as RenderableTrack;
  const rowSummary = tile.isRowBacked ? tile.rowSummary : undefined;
  const recentCommenters =
    (tile.isRowBacked ? rowSummary?.recentCommenters : tile.recentCommenters) ??
    [];
  const uniqueCommenterCount =
    (tile.isRowBacked
      ? rowSummary?.uniqueCommenterCount
      : tile.uniqueCommenterCount) ?? 0;
  const hasCommenters = uniqueCommenterCount > 0 && recentCommenters.length > 0;

  if (hasCommenters) {
    const users: AvatarGroupUser[] = recentCommenters.map((commenter) => ({
      id: commenter.authorId,
      alias: commenter.authorName,
      avatarURL: commenter.authorAvatarUrl ?? undefined,
    }));

    return (
      <button
        type="button"
        className="entity-cta entity-cta--facepile"
        onClick={navigate}
      >
        <UserAvatarGroup
          users={users}
          total={uniqueCommenterCount}
          size={USER_AVATAR_SIZE.MEDIUM}
        />
      </button>
    );
  }

  return (
    <Button
      className="entity-cta"
      theme={BUTTON_THEME.ICON}
      type="button"
      onClick={navigate}
    >
      <ChatSvg />
    </Button>
  );
};

// Space: a "go to space" right-caret CTA. Only below M — above M the expand
// caret already sits on the right of the row, so a second caret would duplicate
// it. Always visible (not hover-gated).
const SpaceCTA = ({ space }: { space: Space }) => {
  const { isMobile } = useViewport();
  const history = useHistory();

  if (!isMobile) return <CellPlaceholder size="medium" />;

  return (
    <Button
      className="entity-cta"
      theme={BUTTON_THEME.ICON}
      type="button"
      onClick={(e) => {
        stopRowClick(e);
        history.push(`/space/${space.id}`);
      }}
    >
      <CaretRightSvg />
    </Button>
  );
};

// URL / file attachment: a top-right arrow that opens the target in a new tab
// (files open their url directly — no in-app rich preview). Hover-only above M;
// always visible below M (no hover on touch).
const AttachmentCTA = ({ file }: { file: FileEntity }) => {
  if (!file.url) return <CellPlaceholder size="medium" />;

  return (
    <div className="entity-cta-hover-only">
      <Button
        className="entity-cta"
        theme={BUTTON_THEME.ICON}
        type="button"
        onClick={(e) => {
          stopRowClick(e);
          window.open(file.url, "_blank");
        }}
      >
        <ArrowUpRightSvg />
      </Button>
    </div>
  );
};

export const EntityCTA = ({ row }: { row: EntityRowConfig }) => {
  // The CTA opens the track's comments, and comments are per
  // `(spaceId, trackId)` — so the deeplink belongs to the space this row is
  // VIEWED under, never `track.spaceId` (the origin, which a borrower often
  // cannot read, and which is absent altogether on a row-only tile). A plain
  // Track with no viewing space falls back to its origin, as before. Mirrors
  // `EntityComments`.
  if (row.type === ENTITY_TYPE.TRACK) {
    const track = row.entity as RenderableTrack;
    const spaceId = row.viewingSpaceId ?? track.spaceId;
    return (
      <TrackCTA
        entity={track}
        href={spaceId ? `/space/${spaceId}/${track.id}` : null}
      />
    );
  }

  if (row.type === ENTITY_TYPE.TRACK_VERSION) {
    const trackVersion = row.entity as Version;
    const spaceId = row.viewingSpaceId ?? trackVersion.track.spaceId;
    return (
      <TrackCTA
        entity={trackVersion}
        href={
          spaceId
            ? `/space/${spaceId}/${trackVersion.track.id}/${trackVersion.id}`
            : null
        }
      />
    );
  }

  if (row.type === ENTITY_TYPE.SPACE) {
    return <SpaceCTA space={row.entity as Space} />;
  }

  if (row.type === ENTITY_TYPE.FILE) {
    return <AttachmentCTA file={row.entity as FileEntity} />;
  }

  // Any other row type has no CTA, but the column is shared — hold its
  // footprint so rows stay aligned.
  return <CellPlaceholder size="medium" />;
};
